import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Plus, MessageSquarePlus, Pencil, Trash2, Mail, Phone,
  Linkedin, PhoneCall, Video, Users as UsersIcon, MailOpen, CheckCircle2, XCircle, Briefcase,
} from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { LogConversationModal } from '../components/crm/LogConversationModal';
import { AccountModal } from '../components/crm/AccountModal';
import { ContactModal } from '../components/crm/ContactModal';
import { TaskModal } from '../components/crm/TaskModal';
import { JobModal } from '../components/crm/JobModal';
import {
  HEALTH_META, statusMeta, priorityMeta, typeLabel, resultLabel,
  fmtDate, fmtDateTime, daysAgoLabel, contactName, jobStatusMeta, salaryRange, feeLabel, fmtMoney,
} from '../components/crm/constants';

const TYPE_ICONS = {
  phone_call: PhoneCall, teams_meeting: Video, personal_meeting: UsersIcon,
  email: MailOpen, linkedin: Linkedin,
};

const Meta = ({ label, value, muted }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    <p className={`text-sm font-medium ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{value}</p>
  </div>
);

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [contactModal, setContactModal] = useState({ open: false, contact: null });
  const [owners, setOwners] = useState([]);

  const load = useCallback(async () => {
    const res = await crmFetch(`/accounts/${id}`);
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
    crmFetch('/sales-users').then((r) => setOwners(r.data || []));
  }, [load]);

  const account = data?.account;
  const contacts = data?.contacts || [];
  const activities = data?.activities || [];
  const tasks = data?.tasks || [];
  const jobs = data?.jobs || [];
  const openTasks = tasks.filter((t) => t.status !== 'completed');
  const lastActivity = activities[0];

  const toggleTask = async (task) => {
    const status = task.status === 'completed' ? 'open' : 'completed';
    const res = await crmFetch(`/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    if (res.success) { toast.success(status === 'completed' ? 'Task completed' : 'Task reopened'); load(); }
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Delete contact ${contactName(contact)}?`)) return;
    const res = await crmFetch(`/contacts/${contact.id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Contact deleted'); load(); }
  };

  const deleteAccount = async () => {
    if (!window.confirm(`Delete account "${account.name}" and all its contacts, activities and tasks?`)) return;
    const res = await crmFetch(`/accounts/${id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Account deleted'); navigate('/accounts'); }
  };

  if (loading) {
    return (
      <SalesLayout>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </SalesLayout>
    );
  }

  if (!account) {
    return (
      <SalesLayout>
        <div className="py-16 text-center text-gray-500">Account not found.</div>
      </SalesLayout>
    );
  }

  const sm = statusMeta(account.status);
  const hm = HEALTH_META[account.health];

  return (
    <SalesLayout>
      <div data-testid="account-detail-page">
        <button onClick={() => navigate('/accounts')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-4" data-testid="back-to-accounts-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Accounts
        </button>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900" data-testid="account-name-heading">{account.name}</h1>
                    <Badge className={`${sm.className} border-0`}>{sm.label}</Badge>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${hm.bg} ${hm.text}`}>
                      <span className={`w-2 h-2 rounded-full ${hm.dot}`} />{hm.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {[account.industry, account.location].filter(Boolean).join(' · ') || 'No details yet'}
                    {account.website && (
                      <> · <a href={account.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>Website</a></>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setContactModal({ open: true, contact: null })} data-testid="add-contact-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Contact
                </Button>
                <Button variant="outline" size="sm" onClick={() => setTaskOpen(true)} data-testid="add-task-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Task
                </Button>
                <Button variant="outline" size="sm" onClick={() => setJobOpen(true)} data-testid="add-job-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Job
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-account-btn">
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700" onClick={deleteAccount} data-testid="delete-account-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setLogOpen(true)} data-testid="detail-log-conversation-btn">
                  <MessageSquarePlus className="w-4 h-4 mr-1.5" /> Log Conversation
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mt-6 pt-5 border-t">
              <Meta label="Account Owner" value={account.ownerName || '—'} />
              <Meta label="Last Contact" value={daysAgoLabel(account.daysSinceContact)} />
              <Meta label="Next Contact" value={account.nextContactDate ? fmtDate(account.nextContactDate) : '—'} />
              <Meta label="Open Tasks" value={openTasks.length} />
              <Meta label="Open Jobs" value={account.openJobCount ?? jobs.filter((j) => j.status === 'open').length} />
              <Meta label="Placements YTD" value={account.placementsYtd ?? '—'} muted={account.placementsYtd == null} />
              <Meta label="Revenue YTD" value={account.revenueYtd != null ? (fmtMoney(account.revenueYtd) || '€0') : '—'} muted={account.revenueYtd == null} />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs ({jobs.length})</TabsTrigger>
            <TabsTrigger value="activities" data-testid="tab-activities">Activities ({activities.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({openTasks.length})</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card data-testid="last-conversation-card">
                <CardHeader className="pb-3"><CardTitle className="text-base">Last Conversation</CardTitle></CardHeader>
                <CardContent>
                  {!lastActivity && <p className="text-sm text-gray-500">No conversations logged yet.</p>}
                  {lastActivity && (
                    <div className="space-y-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{typeLabel(lastActivity.type)}</Badge>
                        {lastActivity.reached
                          ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" />Reached</span>
                          : <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><XCircle className="w-3.5 h-3.5" />Not reached</span>}
                        <span className="text-xs text-gray-400 ml-auto">{fmtDateTime(lastActivity.created_at)}</span>
                      </div>
                      {lastActivity.contact && <p><span className="text-gray-400">Contact:</span> <span className="font-medium">{contactName(lastActivity.contact)}</span></p>}
                      {lastActivity.discussed && <p className="text-gray-700 whitespace-pre-wrap">{lastActivity.discussed}</p>}
                      {lastActivity.result && <div className="flex items-center gap-2"><span className="text-gray-400">Result:</span> <Badge className="bg-blue-100 text-blue-700 border-0">{resultLabel(lastActivity.result)}</Badge></div>}
                      {lastActivity.next_step && <p><span className="text-gray-400">Next step:</span> <span className="font-medium">{lastActivity.next_step}</span></p>}
                      {lastActivity.follow_up_date && <p><span className="text-gray-400">Follow up:</span> <span className="font-medium">{fmtDate(lastActivity.follow_up_date)}</span></p>}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="account-notes-card">
                <CardHeader className="pb-3"><CardTitle className="text-base">Account Notes</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{account.notes || <span className="text-gray-400">No notes yet — click Edit to add some.</span>}</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.length === 0 && (
                <Card className="md:col-span-2 lg:col-span-3"><CardContent className="py-10 text-center text-gray-400 text-sm">No contacts yet — add the first one.</CardContent></Card>
              )}
              {contacts.map((c) => (
                <Card key={c.id} data-testid={`contact-card-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{contactName(c)}</p>
                        <p className="text-xs text-gray-500">{c.job_title || '—'}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setContactModal({ open: true, contact: c })} data-testid={`edit-contact-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => deleteContact(c)} data-testid={`delete-contact-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs text-gray-600">
                      {c.email && <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /><a href={`mailto:${c.email}`} className="hover:text-blue-600">{c.email}</a></p>}
                      {c.phone && <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" />{c.phone}</p>}
                      {c.linkedin_url && <p className="flex items-center gap-2"><Linkedin className="w-3.5 h-3.5 text-gray-400" /><a href={c.linkedin_url} target="_blank" rel="noreferrer" className="hover:text-blue-600">LinkedIn</a></p>}
                      <p className="pt-1 text-gray-400">Last contact: {c.lastContactAt ? fmtDate(c.lastContactAt) : 'Never'}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Jobs */}
          <TabsContent value="jobs">
            <Card>
              <CardContent className="p-4">
                {jobs.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No jobs yet — click “Job” above to add the first opening.</p>}
                <div className="space-y-2">
                  {jobs.map((j) => {
                    const sm = jobStatusMeta(j.status);
                    const sal = salaryRange(j.salary_min, j.salary_max, j.currency);
                    return (
                      <button
                        key={j.id}
                        onClick={() => navigate(`/crm-jobs/${j.id}`)}
                        className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-lg border hover:bg-blue-50/40 transition-colors"
                        data-testid={`account-job-${j.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                          <Briefcase className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{j.title}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {[j.location, sal, feeLabel(j) ? `Fee ${feeLabel(j)}` : null].filter(Boolean).join(' · ') || '—'}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">{j.activeCandidateCount} active</span>
                        <Badge className={`${sm.className} border-0`}>{sm.label}</Badge>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activities */}
          <TabsContent value="activities">
            <Card>
              <CardContent className="p-6">
                {activities.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No activities yet — log your first conversation.</p>}
                <div className="space-y-5">
                  {activities.map((a) => {
                    const Icon = TYPE_ICONS[a.type] || PhoneCall;
                    return (
                      <div key={a.id} className="flex gap-4" data-testid={`activity-item-${a.id}`}>
                        <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0 pb-5 border-b last:border-0">
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-medium text-gray-900">{typeLabel(a.type)}</span>
                            {a.contact && <span className="text-gray-500">with {contactName(a.contact)}</span>}
                            {a.reached === false && <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Not reached</Badge>}
                            {a.result && <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{resultLabel(a.result)}</Badge>}
                            <span className="text-xs text-gray-400 ml-auto">{fmtDateTime(a.created_at)}</span>
                          </div>
                          {a.discussed && <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap">{a.discussed}</p>}
                          {(a.next_step || a.follow_up_date) && (
                            <p className="text-xs text-gray-500 mt-1.5">
                              {a.next_step && <>Next: <span className="font-medium text-gray-700">{a.next_step}</span></>}
                              {a.follow_up_date && <span className="ml-2 text-amber-600 font-medium">Follow up {fmtDate(a.follow_up_date)}</span>}
                            </p>
                          )}
                          {a.logger && <p className="text-[11px] text-gray-400 mt-1">by {`${a.logger.first_name || ''} ${a.logger.last_name || ''}`.trim()}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks */}
          <TabsContent value="tasks">
            <Card>
              <CardContent className="p-4">
                {tasks.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No tasks yet.</p>}
                <div className="space-y-1">
                  {tasks.map((t) => (
                    <div key={t.id} className={`flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 ${t.status === 'completed' ? 'opacity-50' : ''}`} data-testid={`account-task-${t.id}`}>
                      <Checkbox checked={t.status === 'completed'} onCheckedChange={() => toggleTask(t)} data-testid={`account-task-toggle-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium text-gray-900 truncate ${t.status === 'completed' ? 'line-through' : ''}`}>{t.title}</p>
                        {t.contact && <p className="text-xs text-gray-500">{contactName(t.contact)}</p>}
                      </div>
                      <Badge className={`${priorityMeta(t.priority).className} border-0`}>{priorityMeta(t.priority).label}</Badge>
                      <span className={`text-xs whitespace-nowrap ${t.overdue ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
                        {t.due_date ? fmtDate(t.due_date) : 'No date'}{t.overdue ? ' · Overdue' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <LogConversationModal open={logOpen} onOpenChange={setLogOpen} onSaved={load} defaultAccountId={id} />
      <AccountModal open={editOpen} onOpenChange={setEditOpen} onSaved={load} account={account} owners={owners} />
      <ContactModal open={contactModal.open} onOpenChange={(o) => setContactModal((s) => ({ ...s, open: o }))} onSaved={load} accountId={id} contact={contactModal.contact} />
      <TaskModal open={taskOpen} onOpenChange={setTaskOpen} onSaved={load} defaultAccountId={id} />
      <JobModal open={jobOpen} onOpenChange={setJobOpen} onSaved={load} defaultAccountId={id} accountName={account.name} />
    </SalesLayout>
  );
}
